import styled from "styled-components";
import { useEffect, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { captureException } from "@sentry/react";

import { addToStateArray, removeFromStateArray } from "modules/functions";

import Backdrop from "components/other/Backdrop";
import MessageWindow from "components/other/MessageWindow";
import Posts from "./Posts";

const ModalWrap = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
`;

export default () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [switchLoading, setSwitchLoading] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [tutorialBox, setTutorialBox] = useState(false);
  const [page, setPage] = useState(() => {
    const receivedPage = Number(searchParams.get("page"));

    if (receivedPage > 0) return receivedPage;

    return 1;
  });
  const [errorWindow, setErrorWindow] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [filterMenuOpened, setFilterMenuOpened] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => {
    if (localStorage.getItem("posts-status-filter")) {
      const savedFilter = JSON.parse(
        localStorage.getItem("posts-status-filter")
      );

      return savedFilter;
    } else return null;
  });

  const UserProperties = createSelector(
    (state) => state.User,
    (user) => ({
      hasViews: user.hasViews,
      profileData: user.profileData,
    })
  );

  const { hasViews, profileData } = useSelector(UserProperties);

  // Fetch data
  const limit = 3;

  const GET_POSTS_DATA = gql`
    query GetPostsData($page: Int!, $limit: Int, $status: String) {
      posts(page: $page, limit: $limit, status: $status) {
        posts {
          id
          created_at
          name
          status
          on_hold
          switched_on
          text
          image
          image_link
          video
          rejection_reason
          likes
          daily_limit
          over_limit
          ad_plan_features
          posts_stats {
            id
            to
            total_impressions
            total_interactions
            total_spent
          }
          impressions {
            id
            registered_at
            interacted
            cpi
            country
          }
        }
        numOfResults
      }
    }
  `;

  const { loading, data, error, refetch } = useQuery(GET_POSTS_DATA, {
    variables: { page, limit, status: statusFilter },
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("fetching posts error", error);
    captureException(error);
  }

  // handle Post switch
  const TOGGLE_POST_SWITCH = gql`
    mutation TogglePostSwitch($postId: ID!) {
      togglePostSwitch(postId: $postId) {
        id
        status
        switched_on
      }
    }
  `;

  const [togglePostSwitch] = useMutation(TOGGLE_POST_SWITCH);

  const onSwitchClick = async (postId) => {
    try {
      setErrorWindow(false);
      setErrorMessage(null);

      if (!postId) return;

      const post = data.posts.posts.find((post) => post.id === postId);

      if (profileData.adPlan < post.ad_plan_features) {
        // Ad plan has higher ad plan features
        setErrorMessage(
          "The post uses features from a higher advertising plan and cannot be launched. You can edit or duplicate it."
        );
        openErrorWindow();
        return;
      }

      if (switchLoading.includes(postId)) return;

      addToStateArray(switchLoading, postId, setSwitchLoading);

      await togglePostSwitch({
        variables: {
          postId,
        },
      });

      removeFromStateArray(switchLoading, postId, setSwitchLoading);
    } catch (error) {
      captureException(error);
      return;
    }
  };

  // handle Post edit
  const onEditClick = (postId, page) => {
    let navigateTo = `/modify-post?id=${postId}`;

    if (page) navigateTo += `&page=${page}`;

    navigate(navigateTo);
  };

  // handle Post delete
  const onDeleteClick = (postId) => {
    setConfirmDelete(postId);
  };

  const closeDeleteModal = () => {
    setConfirmDelete(null);
  };

  const DELETE_POST = gql`
    mutation DeletePost($postId: ID!) {
      deletePost(postId: $postId) {
        resultCode
      }
    }
  `;

  const [deletePost] = useMutation(DELETE_POST);

  const onConfirmDelete = async () => {
    try {
      if (!confirmDelete) return;

      if (loadingDelete) return;

      setLoadingDelete(true);

      await deletePost({
        variables: {
          postId: confirmDelete,
        },
      });

      refetch();

      setLoadingDelete(false);
      setConfirmDelete(null);
    } catch (error) {
      captureException(error);
      setLoadingDelete(false);
      return;
    }
  };

  // handle Post duplicate
  const onDuplicateClick = (postId) => {
    navigate("/modify-post?id=" + postId + "&duplicate=true");
  };

  // handle Tutorial box
  const closeBox = () => {
    setTutorialBox(false);

    localStorage.setItem("closed-discover-t-box", "y");
  };

  useEffect(() => {
    const closedBox = localStorage.getItem("closed-discover-t-box");

    if (!hasViews && !closedBox) setTutorialBox(true);
    else setTutorialBox(false);
  }, [hasViews]);

  //console.log({ data });

  // handle pagination
  if (data) {
    if (data.posts.numOfResults > 0)
      if (page > Math.ceil(data.posts.numOfResults / limit)) setPage(1);
  }

  const paginate = (where) => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (where === "previous") setPage((prevState) => prevState - 1);
    if (where === "next") setPage((prevState) => prevState + 1);
  };

  // Error handling
  const openErrorWindow = () => {
    setErrorWindow(true);
  };

  const handleModalClose = (modal) => {
    if (modal === "error") {
      setErrorWindow(false);
    }
  };

  // handle Posts filter
  const onFilterMenuClick = () => {
    setFilterMenuOpened((prevState) => !prevState);
  };

  const closeFilterMenu = () => {
    setFilterMenuOpened(false);
  };

  const onFilterSelected = (option) => {
    setStatusFilter(option);
    setPage(1);

    localStorage.setItem("posts-status-filter", JSON.stringify(option));

    setFilterMenuOpened(false);
  };

  return (
    <>
      <Posts
        loading={loading}
        data={data}
        onSwitchClick={onSwitchClick}
        switchLoading={switchLoading}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
        confirmDelete={confirmDelete}
        closeDeleteModal={closeDeleteModal}
        onConfirmDelete={onConfirmDelete}
        loadingDelete={loadingDelete}
        onDuplicateClick={onDuplicateClick}
        tutorialBox={tutorialBox}
        closeBox={closeBox}
        limit={limit}
        paginate={paginate}
        page={page}
        onFilterMenuClick={onFilterMenuClick}
        closeFilterMenu={closeFilterMenu}
        filterMenuOpened={filterMenuOpened}
        onFilterSelected={onFilterSelected}
        statusFilter={statusFilter}
      />
      {errorWindow && (
        <ModalWrap>
          <Backdrop
            onClick={handleModalClose}
            parameters="error"
            background="rgba(0, 0, 0, 0.7)"
          />
          <MessageWindow handleModalClose={handleModalClose} type="error">
            {errorMessage}
          </MessageWindow>
        </ModalWrap>
      )}
    </>
  );
};
